import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { showError, reportErrorToBackend } from '@/utils/errors';
import { is2faChallenge, login2fa, get2faRateLimitMessage, get2faAttemptsRemaining } from '@/utils/twoFactor';
import { use2faLockout, formatLockout } from '@/hooks/use2faLockout';
import { pickWpRef } from '@/utils/wingpointConnect';
import { Mail, Lock, Eye, EyeOff, AlertCircle, X, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.trustoffice.app';

// Use XMLHttpRequest for maximum mobile compatibility
const xhrPost = (url, data) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const send = () => {
      let failureHandled = false;
      // Status 0 / onerror = network-level failure (connection drop,
      // transient DNS/CORS hiccup, ad-blocker). Often transient -- retry once.
      // Browsers fire BOTH onerror and a final readystatechange(status 0)
      // for one failure, so only handle the first signal.
      const retryOrFail = (detail) => {
        if (failureHandled) return;
        failureHandled = true;
        if (attempts < 1) {
          attempts += 1;
          setTimeout(send, 1500);
        } else {
          reject(new Error(detail || 'Network error - please check your connection and try again'));
        }
      };

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
          try {
            const response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else if (xhr.status === 0) {
              retryOrFail(response.detail);
            } else {
              const err = new Error(response.detail || `Request failed with status ${xhr.status}`);
              // Carry the parsed body so callers can detect structured
              // challenges (e.g. 2fa_required) on non-2xx responses.
              err.payload = xhr.responseText;
              reject(err);
            }
          } catch (e) {
            reject(new Error('Invalid server response'));
          }
        }
      };

      xhr.onerror = function() {
        retryOrFail();
      };

      xhr.send(JSON.stringify(data));
    };

    send();
  });
};

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser, loadTrusts, loadSubscriptionState } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // 2FA second-step state: after a password-only login returns
  // 401 {detail:'2fa_required', challenge_token}, collect the 6-digit code
  // (or a recovery code) and POST /auth/2fa/login.
  const [twoFactorStage, setTwoFactorStage] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorAttemptsLeft, setTwoFactorAttemptsLeft] = useState(null);
  const lockout = use2faLockout();

  // Redirect if already logged in -- use wp-aware routing so params are preserved
  useEffect(() => {
    if (!user) return;
    const wp = searchParams.get('wp');
    const action = searchParams.get('action');
    const invite = searchParams.get('invite');
    if (invite) {
      navigate(`/invite/${encodeURIComponent(invite)}`, { replace: true });
      return;
    }
    if (wp === '1' && action) {
      const coupon = searchParams.get('coupon');
      const plan = searchParams.get('plan');
      // Keep the WingPoint provisioning ref alive through navigation (2026-10-28):
      // it arrives as `ref` (admin template) or `wp_ref` (connect handoffs) and
      // must reach pricing → checkout, or the eligibility guard 403s legacy
      // accounts. Only well-formed refs are carried (see pickWpRef).
      const wpRef = pickWpRef([searchParams.get('wp_ref'), searchParams.get('ref')]);
      if (wpRef) {
        try { sessionStorage.setItem('wp_ref', wpRef); } catch (e) { /* storage blocked */ }
      }
      if (action === 'subscribe') {
        const params = new URLSearchParams({ wp: '1', action: 'subscribe' });
        if (coupon) params.set('coupon', coupon);
        if (plan) params.set('plan', plan);
        if (wpRef) params.set('wp_ref', wpRef);
        navigate(`/pricing?${params.toString()}`, { replace: true });
      } else if (action === 'upgrade') {
        const params = new URLSearchParams({ wp: '1', action: 'upgrade' });
        if (plan) params.set('plan', plan);
        if (wpRef) params.set('wp_ref', wpRef);
        navigate(`/settings/billing?${params.toString()}`, { replace: true });
      } else if (action === 'resubscribe') {
        navigate('/settings/billing?wp=1&action=resubscribe', { replace: true });
      } else if (action === 'update_payment') {
        navigate('/settings/billing?wp=1&action=update_payment', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [user, searchParams, navigate]);

  // Shared post-login success path used by BOTH the normal login and the
  // 2FA second step, so a 2FA login proceeds exactly like a normal one.
  const completeLogin = async (data) => {
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
    }
    if (data.user) {
      setUser(data.user);
    }

    // Load trusts and subscription state after successful authentication
    await Promise.all([
      loadTrusts(),
      loadSubscriptionState(data.user?.email || data.user?.name)
    ]);

    toast.success('Welcome back');

    // WingPoint-aware routing: check URL params for wp=1 and action
    const wp = searchParams.get('wp');
    const action = searchParams.get('action');
    const invite = searchParams.get('invite');
    if (invite) {
      navigate(`/invite/${encodeURIComponent(invite)}`, { replace: true });
      return;
    }
    if (wp === '1' && action) {
      const coupon = searchParams.get('coupon');
      const plan = searchParams.get('plan');
      // Post-login: keep the WingPoint provisioning ref with the flow
      // (2026-10-28) — same reason as the redirect guard above. The ref
      // reaches pricing → checkout so the eligibility guard can verify it
      // against the provisioning ledger; without this, legacy accounts
      // without marker fields hit 403 on the wingpoint plan.
      const wpRef = pickWpRef([searchParams.get('wp_ref'), searchParams.get('ref')]);
      if (wpRef) {
        try { sessionStorage.setItem('wp_ref', wpRef); } catch (e) { /* storage blocked */ }
      }
      if (action === 'subscribe') {
        const params = new URLSearchParams({ wp: '1', action: 'subscribe' });
        if (coupon) params.set('coupon', coupon);
        if (plan) params.set('plan', plan);
        if (wpRef) params.set('wp_ref', wpRef);
        navigate(`/pricing?${params.toString()}`, { replace: true });
      } else if (action === 'upgrade') {
        const params = new URLSearchParams({ wp: '1', action: 'upgrade' });
        if (plan) params.set('plan', plan);
        if (wpRef) params.set('wp_ref', wpRef);
        navigate(`/settings/billing?${params.toString()}`, { replace: true });
      } else if (action === 'resubscribe') {
        navigate('/settings/billing?wp=1&action=resubscribe', { replace: true });
      } else if (action === 'update_payment') {
        navigate('/settings/billing?wp=1&action=update_payment', { replace: true });
      } else if (action === 'welcome') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loading) return;
    
    // Clear previous errors
    setLoginError('');
    
    // Basic validation
    if (!email.trim()) {
      setLoginError('Please enter your email address.');
      return;
    }
    if (!password) {
      setLoginError('Please enter your password.');
      return;
    }
    
    setLoading(true);
    setLoginError('');
    
    try {
      const data = await xhrPost(`${API_URL}/api/auth/login`, {
        email: email.trim().toLowerCase(),
        password: password
      });

      if (is2faChallenge(data)) {
        // Password accepted; backend wants the TOTP (or recovery) code next.
        // NOTE: xhrPost rejects on non-2xx, so reaching here means the backend
        // answered 200 with a challenge payload (defensive -- the primary
        // detection for 401 bodies happens in the catch below).
        setChallengeToken(data.challenge_token);
        setTwoFactorStage(true);
        setLoginError('');
        return;
      }

      await completeLogin(data);
    } catch (error) {
      const rawMsg = error.message || 'Login failed';

      // 2FA challenge: the backend returns 401 with a JSON body
      // {detail:'2fa_required', challenge_token}. xhrPost rejects with the
      // detail string, so sniff the message; if this account has 2FA enabled
      // the password was correct and we move to the code-entry step.
      if (rawMsg.includes('2fa_required')) {
        try {
          const parsed = JSON.parse(error.payload || 'null');
          if (is2faChallenge(parsed)) {
            setChallengeToken(parsed.challenge_token);
            setTwoFactorStage(true);
            setLoginError('');
            setTwoFactorError('');
            return;
          }
        } catch (parseErr) {
          // fall through to the generic error path
        }
      }

      // Provide more user-friendly messages for common error cases
      let friendlyMsg = rawMsg;
      if (rawMsg.includes('Network error')) {
        friendlyMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (rawMsg.includes('Invalid server response')) {
        friendlyMsg = 'The server returned an unexpected response. Please try again or contact support if the problem persists.';
      } else if (rawMsg.includes('401') || rawMsg.toLowerCase().includes('invalid credentials')) {
        friendlyMsg = 'Incorrect email or password. Please verify your credentials and try again.';
      } else if (rawMsg.includes('403') || rawMsg.toLowerCase().includes('not verified')) {
        friendlyMsg = 'Your email address has not been verified. Please check your inbox for a verification email.';
      }
      setLoginError(friendlyMsg);
      toast.error(friendlyMsg);
      // Report login errors to backend (except invalid credentials -- too noisy)
      if (!rawMsg.includes('401') && !rawMsg.toLowerCase().includes('invalid credentials')) {
        reportErrorToBackend(error, { operation: 'login', page: 'Login' });
      }
    } finally {
      setLoading(false);
    }
  };

  // 2FA second step: exchange the challenge token + 6-digit code (or a
  // recovery code) for a normal session, then proceed exactly as a normal
  // login would.
  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const trimmed = twoFactorCode.trim();
    if (!trimmed) {
      setTwoFactorError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setTwoFactorError('');
    setTwoFactorAttemptsLeft(null);
    try {
      const data = await login2fa(challengeToken, trimmed);
      setTwoFactorCode('');
      lockout.clear();
      setTwoFactorAttemptsLeft(null);
      await completeLogin(data);
    } catch (error) {
      const rawMsg = error.message || 'Verification failed';
      const rateMsg = get2faRateLimitMessage(error);
      const attemptsLeft = get2faAttemptsRemaining(error);
      let friendlyMsg = rawMsg;
      if (rateMsg) {
        // Backend rate-limit: show its message verbatim and start the countdown.
        const retryAfter = Number(error?.body?.retry_after || error?.payload?.retry_after || 0);
        lockout.trigger(retryAfter);
        friendlyMsg = rateMsg;
      } else if (rawMsg.includes('Network error')) {
        friendlyMsg = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (rawMsg.includes('401') || rawMsg.toLowerCase().includes('invalid') || rawMsg.toLowerCase().includes('expired')) {
        friendlyMsg = 'That code was not valid or has expired. Please check your authenticator app and try again.';
      }
      setTwoFactorError(friendlyMsg);
      setTwoFactorAttemptsLeft(attemptsLeft);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Use TrustOffice's own Google OAuth (branded consent screen)
    const redirectAfter = '/dashboard';
    window.location.href = `${API_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectAfter)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Skip-to-content link for keyboard / screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-navy focus:text-white"
      >
        Skip to main content
      </a>
      {/* Left side - Texture with overlay */}
      <div className="hidden lg:flex lg:w-1/2 login-texture relative">
        <div className="absolute inset-0 login-overlay flex flex-col justify-center items-center p-12">
          <img 
            src="/assets/trustoffice-logo-vertical.svg?v=3"
            alt="TrustOffice"
            className="w-48 mb-8"
          />
          <p className="text-white/80 font-mono text-xs uppercase tracking-widest text-center max-w-md">
            Trust Governance Workspace
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div id="main-content" className="flex-1 flex items-center justify-center p-8 bg-subtle-bg">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-12 text-center">
            <img 
              src="/assets/trustoffice-logo.svg?v=3"
              alt="TrustOffice"
              className="h-10 mx-auto"
            />
          </div>

          {/* Login card with corner marks */}
          <div className="card-trust corner-mark relative">
            {twoFactorStage ? (
              <>
                <h1 className="font-serif text-3xl text-navy mb-2">Two-Factor Verification</h1>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8">
                  Protecting your account
                </p>

                <form onSubmit={handleTwoFactorSubmit} data-testid="twofa-login-step">
                  <div className="space-y-4">
                    {twoFactorError && (
                      <div className="p-3 bg-error/10 border border-error/20 flex items-start gap-2" data-testid="twofa-login-error">
                        <AlertCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-error">Verification failed</p>
                          <p className="text-xs text-error/80 mt-0.5">{twoFactorError}</p>
                        </div>
                      </div>
                    )}
                    {lockout.active && (
                      <div className="p-3 bg-navy/5 border border-navy/20 flex items-center gap-2" data-testid="twofa-login-countdown" role="status">
                        <AlertCircle className="w-4 h-4 text-navy flex-shrink-0" />
                        <p className="text-sm text-navy">
                          You can try again in <span className="font-mono">{formatLockout(lockout.secondsLeft)}</span>
                        </p>
                      </div>
                    )}
                    <div className="flex items-start gap-3 p-3 bg-navy/5">
                      <Shield className="w-4 h-4 text-navy mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-navy">
                        Open your authenticator app and enter the 6-digit code it shows
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          or use a recovery code if you lost your device.
                        </span>
                      </p>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Authentication Code
                      </Label>
                      <Input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        placeholder="123456"
                        maxLength={40}
                        value={twoFactorCode}
                        onChange={(e) => { setTwoFactorCode(e.target.value); if (twoFactorError) setTwoFactorError(''); }}
                        className="input-trust mt-1 tracking-widest text-center text-lg font-mono"
                        aria-label="6-digit authentication code or recovery code"
                        data-testid="twofa-login-code-input"
                        disabled={lockout.active}
                      />
                    </div>
                    {twoFactorAttemptsLeft != null && (
                      <p className="text-xs text-muted-foreground" data-testid="twofa-login-attempts-left">
                        <span className="font-mono">{twoFactorAttemptsLeft}</span> attempts left before a short cooldown
                      </p>
                    )}
                    <Button
                      type="submit"
                      className="w-full btn-primary h-12"
                      disabled={loading || lockout.active}
                      data-testid="twofa-login-submit-btn"
                    >
                      {loading ? 'Verifying...' : 'Verify Code'}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h1 className="font-serif text-3xl text-navy mb-2">Sign In</h1>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8">
                  Sign in to your account
                </p>

                {/* Session expired banner -- when redirected from a 401 mid-session */}
                {searchParams.get('reason') === 'session-expired' && (
                  <div className="mb-6 bg-gold/10 border border-gold/30 p-4 text-navy" data-testid="session-expired-banner">
                    <p className="text-sm">
                      Your session has expired. Please sign in again to continue.
                    </p>
                  </div>
                )}

                {/* WingPoint welcome-back banner -- only when ?wp=1 present */}
                {searchParams.get('wp') === '1' && (
                  <div className="mb-6 bg-gold/10 border border-gold/30 p-4 text-navy" data-testid="wp-welcome-banner">
                    <p className="text-sm">
                      Welcome back. Your WingPoint trust is waiting in your TrustOffice account. Log in to continue setting up your management plan.
                    </p>
                  </div>
                )}

                {/* Google login button */}
                <Button
                  onClick={handleGoogleLogin}
                  className="w-full mb-6 bg-white border border-navy/20 text-navy hover:bg-navy hover:text-white font-sans uppercase tracking-wider text-xs h-12"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                {/* Divider */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-1 h-px bg-navy/10"></div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Or</span>
                  <div className="flex-1 h-px bg-navy/10"></div>
                </div>

                {/* Email login form */}
                <form onSubmit={handleEmailLogin}>
                  <div className="space-y-4">
                    {/* Error state */}
                    {loginError && (
                      <div className="p-3 bg-error/10 border border-error/20 flex items-start gap-2" data-testid="login-error">
                        <AlertCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-error">Sign-in failed</p>
                          <p className="text-xs text-error/80 mt-0.5">{loginError}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setLoginError('')}
                          className="text-error/60 hover:text-error flex-shrink-0"
                          aria-label="Dismiss error"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <div>
                      <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Email Address
                      </Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="email"
                          autoComplete="username"
                          inputMode="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); if (loginError) setLoginError(''); }}
                          className={`pl-10 input-trust ${loginError ? 'border-error/40' : ''}`}
                          placeholder="your@email.com"
                          aria-label="Email address"
                          required
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          Password
                        </Label>
                        <Link
                          to="/forgot-password"
                          className="text-xs text-navy hover:text-navy/70"
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); if (loginError) setLoginError(''); }}
                          className={`pl-10 pr-10 input-trust ${loginError ? 'border-error/40' : ''}`}
                          placeholder="Enter password"
                          aria-label="Password"
                          required
                          data-testid="password-input"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full btn-primary h-12"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </div>
                </form>
              </>
            )}

            {!twoFactorStage && (
              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to={(() => {
                    // Forward WingPoint attribution params to the signup page.
                    // pickWpRef accepts both `wp_ref` and the template's `ref`
                    // (WP-XXXXXXXX-XXXX) so "Create account" keeps attribution
                    // alive into /pricing (2026-10-28).
                    const wpRef = pickWpRef([searchParams.get('wp_ref'), searchParams.get('ref')]);
                    const trustName = searchParams.get('trust_name');
                    const plan = searchParams.get('plan');
                    const coupon = searchParams.get('coupon');
                    const isWingPoint = wpRef || plan === 'wingpoint';
                    if (isWingPoint) {
                      const params = new URLSearchParams();
                      params.set('ref', 'wp');
                      if (wpRef) params.set('wp_ref', wpRef);
                      if (trustName) params.set('trust_name', trustName);
                      if (coupon) params.set('coupon', coupon);
                      return `/signup?${params.toString()}`;
                    }
                    return '/signup';
                  })()} className="text-navy font-medium hover:text-navy/70" data-testid="signup-link">
                    Create Account
                  </Link>
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Powered by TrustOffice
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
